import AsideCard from '@/components/patterns/aside-card'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useFormSection } from '@/hooks/useFormSection'
import { deliveryCutoffOptions } from '@/lib/delivery-cutoff-options'
import dayjs from '@/lib/dayjs'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { useFormContext } from '@/lib/form-contexts'
import { useLocationFulfillmentPreview } from '@/lib/services/locations/locations.query'
import { useParams } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, XCircle } from 'lucide-react'
import { useEffect, useId, useMemo, useState } from 'react'

const EXAMPLE_CLOSE_MINUTES = 17 * 60 // 5:00 PM

// Format minutes-from-midnight into "h:mmAM" / "h:mmPM" (no space), capitalized.
const formatTimeCompact = (minutesFromMidnight) => {
  const normalized = ((minutesFromMidnight % 1440) + 1440) % 1440
  const hour24 = Math.floor(normalized / 60)
  const minutes = normalized % 60
  const hour12 = ((hour24 + 11) % 12) + 1
  const suffix = hour24 >= 12 ? 'PM' : 'AM'
  return `${hour12}:${minutes.toString().padStart(2, '0')}${suffix}`
}

function CutoffSummaryContainer({ isEnabled, cutoff, fulfillmentType, hours }) {
  const { locationId } = useParams({ strict: false })
  const canPreview = Boolean(locationId)

  // useMemo is required here so we don't create a new payload object every render
  // (which would re-trigger the debouncing effect even when values haven't changed).
  const payload = useMemo(() => {
    const base = {
      fulfillment_type: fulfillmentType,
      is_enabled: Boolean(isEnabled),
      cutoff_hours: Number(cutoff || 0),
      // This is an "example" preview in settings, not a promise.
      ignore_availability_changes: true,
    }

    // Only send the relevant hours config for this fulfillment type.
    if (fulfillmentType === FulfillmentType.Pickup.Value) {
      return { ...base, open_hours: hours ?? [] }
    }

    return { ...base, delivery_hours: hours ?? [] }
  }, [cutoff, fulfillmentType, hours, isEnabled])

  const [debouncedPayload, setDebouncedPayload] = useState(payload)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedPayload(payload), 250)
    return () => clearTimeout(t)
  }, [payload])

  const previewQuery = useLocationFulfillmentPreview(locationId, debouncedPayload, {
    enabled: Boolean(
      canPreview && debouncedPayload?.is_enabled && debouncedPayload?.cutoff_hours >= 1
    ),
    staleTime: 30_000,
  })

  return (
    <CutoffSummary
      isEnabled={isEnabled}
      cutoff={cutoff}
      fulfillmentType={fulfillmentType}
      previewDate={previewQuery.data?.earliest_date ?? null}
      isPreviewLoading={previewQuery.isFetching}
      canPreview={canPreview}
    />
  )
}

// Summary component that shows cutoff examples
const CutoffSummary = ({
  isEnabled,
  cutoff,
  fulfillmentType,
  previewDate,
  isPreviewLoading,
  canPreview,
}) => {
  const [exampleIndex, setExampleIndex] = useState(0)
  const typeLabel = fulfillmentType === FulfillmentType.Pickup.Value ? 'pickup' : 'delivery'

  if (!isEnabled) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <XCircle className="size-4 shrink-0" />
        <Typography variant="label">This location does not offer {typeLabel} services.</Typography>
      </div>
    )
  }

  const cutoffHours = Number(cutoff || 0)
  const isDelivery = fulfillmentType === FulfillmentType.Delivery.Value
  const earliestLabel = isDelivery ? 'Earliest Delivery' : 'Earliest Pickup'

  // For large cutoffs, the "prep time" toggle is more confusing than helpful.
  // Show only the backend-accurate "If ordered now" preview.
  if (cutoffHours > 7) {
    return (
      <div className="space-y-1">
        <Typography variant="label" tone="muted" className="ml-8">
          Example:
        </Typography>
        <div className="text-sm text-muted-foreground ml-8">
          <span className="font-medium text-foreground">If ordered now:</span>{' '}
          {!canPreview ? (
            <span className="text-muted-foreground">Save to preview</span>
          ) : isPreviewLoading ? (
            <span className="text-muted-foreground">Calculating…</span>
          ) : previewDate ? (
            <>
              <span className="text-muted-foreground">{earliestLabel} is </span>
              <span className="font-medium text-foreground">
                {dayjs(previewDate, 'YYYY-MM-DD').format('ddd, MMM D')}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">No available date found.</span>
          )}
        </div>
      </div>
    )
  }

  const examples = [
    {
      title: 'Prep time',
      content: (
        <>
          <span className="font-medium text-foreground">{cutoffHours} hour(s)</span>
          <span className="text-muted-foreground">{' | '}Order by </span>
          <span className="font-medium text-foreground">
            {formatTimeCompact(EXAMPLE_CLOSE_MINUTES - cutoffHours * 60 - 1)}
          </span>
          <span className="text-muted-foreground"> for same day {typeLabel}</span>
        </>
      ),
    },
    {
      title: 'If ordered now',
      content: (
        <>
          {!canPreview ? (
            <span className="text-muted-foreground">Save to preview</span>
          ) : isPreviewLoading ? (
            <span className="text-muted-foreground">Calculating…</span>
          ) : previewDate ? (
            <>
              <span className="text-muted-foreground">{earliestLabel} is </span>
              <span className="font-medium text-foreground">
                {dayjs(previewDate, 'YYYY-MM-DD').format('ddd, MMM D')}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">No available date found.</span>
          )}
        </>
      ),
    },
  ]

  const toggleExample = () => {
    setExampleIndex((prev) => (prev + 1) % examples.length)
  }

  return (
    <div className="space-y-1">
      <Typography variant="label" tone="muted" className="ml-8">
        Example:
      </Typography>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={toggleExample}
        >
          <ChevronLeft className="h-3 w-3" />
        </Button>
        <div className="text-sm text-muted-foreground flex-1 min-w-0">
          <span className="font-medium text-foreground">{examples[exampleIndex].title}:</span>{' '}
          {examples[exampleIndex].content}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={toggleExample}
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}

const FulfillmentSettingsInputs = ({
  icon,
  fulfillmentSettings = {
    fulfillment_type: FulfillmentType.Pickup.Value,
    is_fulfillment_enabled: false,
    cutoff: 0,
  },
  title = '',
  subtitle = '',
}) => {
  const id = useId()
  const form = useFormContext()

  const isPickup = fulfillmentSettings.fulfillment_type === FulfillmentType.Pickup.Value
  const enabledFieldName = isPickup ? 'is_pickup' : 'is_delivery'
  const cutoffFieldName = isPickup ? 'pickup_cutoff' : 'delivery_cutoff'
  const hoursFieldName = isPickup ? 'open_hours' : 'delivery_hours'

  // Use the form section hook to manage this section's data
  useFormSection({
    fieldName: enabledFieldName,
    initialData: fulfillmentSettings.is_fulfillment_enabled,
    getDefaultData: () => false,
  })

  useFormSection({
    fieldName: cutoffFieldName,
    initialData: fulfillmentSettings.cutoff,
    getDefaultData: () => (isPickup ? 2 : 4),
  })

  return (
    <AsideCard
      title={
        title ? (
          <span className="inline-flex items-center gap-2">
            {icon}
            {title}
          </span>
        ) : null
      }
      description={subtitle || null}
      aside={
        <form.Subscribe
          selector={(state) => [
            state.values[enabledFieldName],
            state.values[cutoffFieldName],
            state.values[hoursFieldName],
          ]}
        >
          {([isEnabled, cutoff, hours]) => (
            <CutoffSummaryContainer
              isEnabled={isEnabled}
              cutoff={cutoff || (isPickup ? 2 : 4)}
              hours={hours}
              fulfillmentType={fulfillmentSettings.fulfillment_type}
            />
          )}
        </form.Subscribe>
      }
      asideWidth="25%"
    >
      <div className="@container">
        <div className="flex flex-wrap @md:flex-nowrap @lg:justify-around items-center gap-4">
          <form.AppField name={enabledFieldName}>
            {(field) => (
              <field.SwitchInput
                id={`${id}-is-fulfillment-enabled`}
                label={`Allow ${isPickup ? FulfillmentType.Pickup.Name : FulfillmentType.Delivery.Name}`}
                className="w-full shrink-0 @md:w-fit @md:mr-4 "
              />
            )}
          </form.AppField>
          <form.AppField name={cutoffFieldName}>
            {(field) => (
              <field.SelectInput
                className="w-full @lg:w-fit"
                id={`${id}-cutoff`}
                label={
                  <div className="flex flex-col gap-1 mb-1">
                    <div>
                      {isPickup ? FulfillmentType.Pickup.Name : FulfillmentType.Delivery.Name}{' '}
                      Cutoff
                    </div>
                    <div>
                      <Typography variant="label" tone="muted">
                        (Required <b>business hours/days</b> to prep a{' '}
                        {isPickup ? 'pickup' : 'delivery'})
                      </Typography>
                    </div>
                  </div>
                }
                options={deliveryCutoffOptions}
              />
            )}
          </form.AppField>
        </div>
      </div>
    </AsideCard>
  )
}

export default FulfillmentSettingsInputs
